import React from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { Sparkles } from 'lucide-react';
import { SectionCard } from '../common/SectionCard';
import { Progress } from '../ui/Progress';
import { formatCompact, formatNumber } from '../../utils/format';
import { tierById } from '../../data/outreach';
import type { OutreachCampaign } from '../../types/outreach';
export function ReachAnalytics({
  campaign
}: {
  campaign: OutreachCampaign;
}) {
  const tier = tierById(campaign.tier);
  const guarantee = tier?.impressionGuarantee ?? 0;
  const delivered = guarantee ? Math.round(campaign.impressions / guarantee * 100) : 0;
  return <div className="space-y-4" data-id="element-794" data-mp="1mzpnrzjxgoxf">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-id="element-795" data-mp="1myofu3y7kijx">
        <div className="rounded-sm border border-border bg-card px-4 py-3" data-id="element-796" data-mp="3juvlkshrjf9y">
          <p className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-797" data-mp="3orm3lld4oky0">
            Impressions delivered
          </p>
          <p className="mt-1 font-mono text-xl font-semibold" data-id="element-798" data-mp="3eas7r5daurh1">
            {formatCompact(campaign.impressions)}
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground" data-id="element-799" data-mp="34jnrm2bw10km">
            {delivered}% of {formatCompact(guarantee)} guarantee
          </p>
          <div className="mt-2" data-id="element-800" data-mp="30j3xz8qiwf38">
            <Progress value={Math.min(delivered, 100)} data-id="element-801" data-mp="30fzk1jn9a4zd" />
          </div>
        </div>
        <div className="rounded-sm border border-border bg-card px-4 py-3" data-id="element-802" data-mp="12fefvs3uow8">
          <p className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-803" data-mp="3sabepuudjwrr">
            Engagements
          </p>
          <p className="mt-1 font-mono text-xl font-semibold" data-id="element-804" data-mp="3o5tw3yzr3pik">
            {formatNumber(campaign.engagements)}
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground" data-id="element-805" data-mp="2cshyi5ul434u">
            {(campaign.engagements / campaign.impressions * 100).toFixed(2)}% engagement rate
          </p>
        </div>
        <div className="rounded-sm border border-border bg-card px-4 py-3" data-id="element-806" data-mp="27vrjzmyfhflj">
          <p className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-807" data-mp="29b3rzljnqpvo">
            Referral traffic
          </p>
          <p className="mt-1 font-mono text-xl font-semibold" data-id="element-808" data-mp="209w3fv6y13es">
            {formatNumber(campaign.referrals)}
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground" data-id="element-809" data-mp="24ay32d4rvhha">
            Clicks to the organiser ticketing page
          </p>
        </div>
        <div className="rounded-sm border border-border bg-card px-4 py-3" data-id="element-810" data-mp="1qihukwt3qr8z">
          <p className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-811" data-mp="1vbuif4pyiu0o">
            Overseas share
          </p>
          <p className="mt-1 font-mono text-xl font-semibold" data-id="element-812" data-mp="1gnpyxyj2yegu">
            {100 - (campaign.geography.find(g => g.market === 'UAE')?.share ?? 0)}%
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground" data-id="element-813" data-mp="17m9oxrw6ged5">
            Audience reached outside the UAE
          </p>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.5fr_1fr]" data-id="element-814" data-mp="1cm4ftvfyd3y7">
        <SectionCard title="Weekly delivery" description="Impressions and engagements by campaign week" data-id="element-815" data-mp="szaz21cyxs0j">
          <div className="h-60 w-full" data-id="element-816" data-mp="o1j5z40l7zur">
            <ResponsiveContainer width="100%" height="100%" data-id="element-817" data-mp="11zg80fxt8ygt">
              <AreaChart data={campaign.weekly} margin={{
              top: 4,
              right: 8,
              left: -18,
              bottom: 0
            }} data-id="element-818" data-mp="11wv3tka1i6fw">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} data-id="element-819" data-mp="xzyzter01n7y" />
                <XAxis dataKey="week" tick={{
                fontSize: 11,
                fill: 'var(--muted-foreground)'
              }} tickLine={false} axisLine={{
                stroke: 'var(--border)'
              }} data-id="element-820" data-mp="1bv12lt0juwgj" />
                <YAxis tick={{
                fontSize: 11,
                fill: 'var(--muted-foreground)'
              }} tickFormatter={(value: number) => formatCompact(value)} tickLine={false} axisLine={false} data-id="element-821" data-mp="1gnu8wlbo2cuq" />
                <ChartTooltip contentStyle={{
                fontSize: 12,
                borderRadius: 4,
                border: '1px solid var(--border)'
              }} formatter={(value: number) => formatNumber(value)} data-id="element-822" data-mp="1pmxsdqeqwu9r" />
                <Area type="monotone" dataKey="impressions" stroke="var(--chart-1)" fill="var(--chart-1)" fillOpacity={0.14} strokeWidth={2} data-id="element-823" data-mp="3tv2aqi5ac1pp" />
                <Area type="monotone" dataKey="engagements" stroke="var(--chart-3)" fill="var(--chart-3)" fillOpacity={0.16} strokeWidth={2} data-id="element-824" data-mp="3187rtpo49aec" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Geographic distribution" description="Share of audience by market" data-id="element-825" data-mp="pfkcxajraghh">
          <div className="h-60 w-full" data-id="element-826" data-mp="uenhyuzqkmpg">
            <ResponsiveContainer width="100%" height="100%" data-id="element-827" data-mp="uhrrm7vz0ani">
              <BarChart data={campaign.geography} layout="vertical" margin={{
              top: 0,
              right: 16,
              left: 26,
              bottom: 0
            }} data-id="element-828" data-mp="14iaku5nnyi2o">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} data-id="element-829" data-mp="ut0rynkzwvdi" />
                <XAxis type="number" unit="%" tick={{
                fontSize: 11,
                fill: 'var(--muted-foreground)'
              }} tickLine={false} axisLine={false} data-id="element-830" data-mp="qt0dx41t6m6z" />
                <YAxis type="category" dataKey="market" width={80} tick={{
                fontSize: 11,
                fill: 'var(--muted-foreground)'
              }} tickLine={false} axisLine={false} data-id="element-831" data-mp="gw448v06bu0u" />
                <ChartTooltip contentStyle={{
                fontSize: 12,
                borderRadius: 4,
                border: '1px solid var(--border)'
              }} formatter={(value: number) => `${value}%`} data-id="element-832" data-mp="268fpptl13r9o" />
                <Bar dataKey="share" fill="var(--chart-2)" radius={[0, 2, 2, 0]} data-id="element-833" data-mp="3vos22zxnyx0k" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      <section className="rounded-sm bg-uae-green p-4 text-white" data-id="element-834" data-mp="3mqzlztk4blc4">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/60" data-id="element-835" data-mp="3qq8g4zj2ll87">
          <Sparkles className="h-3.5 w-3.5" aria-hidden="true" data-id="element-836" data-mp="ichvhlckox3s" />
          AI performance note
        </p>
        <p className="mt-2 max-w-4xl text-[13px] leading-relaxed text-white/85" data-id="element-837" data-mp="n9rjqxo872q1">
          {campaign.aiNote}
        </p>
      </section>
    </div>;
}